import emojisJson from "@/src/shared/emoji/data/emojis.json";
import { EMOJI_CATEGORY, EMOJI_SKIN_TONE, EMOJI_SKIN_TONE_MODIFIERS } from "@/src/shared/emoji/constants";
import type {
  EmojiCategory,
  EmojiData,
  EmojiDataCategory,
  EmojiDataItem,
  EmojiDisplayItem,
  EmojiSkinTone,
  FilterEmojisOptions,
} from "@/src/shared/emoji/types";

const UNICODE_PART_PATTERN = /^[0-9a-f]{1,6}$/;
const emojisData = emojisJson as EmojiData;

const isDataCategory = (category: EmojiCategory): category is EmojiDataCategory => {
  return category !== EMOJI_CATEGORY.Recent && category !== EMOJI_CATEGORY.All;
};

export const normalizeEmojiUnicode = (unicode: unknown): string | null => {
  if (typeof unicode !== "string") return null;

  const normalized = unicode.trim().toLowerCase();
  const parts = normalized.split("-");

  if (!normalized || parts.some(part => !UNICODE_PART_PATTERN.test(part) || Number.parseInt(part, 16) > 0x10ffff)) {
    return null;
  }

  return parts.map(part => Number.parseInt(part, 16).toString(16)).join("-");
};

export const unicodeToNativeEmoji = (unicode: string): string | null => {
  const normalized = normalizeEmojiUnicode(unicode);
  if (!normalized) return null;

  return normalized
    .split("-")
    .map(part => String.fromCodePoint(Number.parseInt(part, 16)))
    .join("");
};

const toDisplayItem = (
  emoji: EmojiDataItem,
  category: EmojiDataCategory,
  unicode = emoji.u
): EmojiDisplayItem | null => {
  const normalizedUnicode = normalizeEmojiUnicode(unicode);
  const native = normalizedUnicode ? unicodeToNativeEmoji(normalizedUnicode) : null;
  if (!normalizedUnicode || !native) return null;

  return {
    aliases: emoji.n,
    category,
    native,
    unicode: normalizedUnicode,
    variants: emoji.v ?? [],
  };
};

let allEmojisCache: EmojiDisplayItem[] | undefined;
let emojiByUnicodeCache: Map<string, EmojiDisplayItem> | undefined;
let neutralEmojiByVariantCache: Map<string, EmojiDisplayItem> | undefined;

const getAllEmojis = (): EmojiDisplayItem[] => {
  allEmojisCache ??= Object.entries(emojisData).flatMap(([category, emojis]) => {
    return emojis.flatMap(emoji => {
      const item = toDisplayItem(emoji, category as EmojiDataCategory);
      return item ? [item] : [];
    });
  });

  return allEmojisCache;
};

const getEmojiByUnicode = (): Map<string, EmojiDisplayItem> => {
  if (emojiByUnicodeCache) return emojiByUnicodeCache;

  const emojiByUnicode = new Map<string, EmojiDisplayItem>();
  const neutralEmojiByVariant = new Map<string, EmojiDisplayItem>();
  getAllEmojis().forEach(emoji => {
    emojiByUnicode.set(emoji.unicode, emoji);
    emoji.variants.forEach(variant => {
      const variantItem = toDisplayItem(
        { n: emoji.aliases, u: emoji.unicode, v: emoji.variants },
        emoji.category,
        variant
      );
      if (variantItem) {
        emojiByUnicode.set(variantItem.unicode, variantItem);
        neutralEmojiByVariant.set(variantItem.unicode, emoji);
      }
    });
  });

  emojiByUnicodeCache = emojiByUnicode;
  neutralEmojiByVariantCache = neutralEmojiByVariant;
  return emojiByUnicode;
};

export const findEmojiByUnicode = (unicode: unknown): EmojiDisplayItem | null => {
  const normalized = normalizeEmojiUnicode(unicode);
  return normalized ? (getEmojiByUnicode().get(normalized) ?? null) : null;
};

export const resolveEmojiUnicodes = (unicodes: unknown): EmojiDisplayItem[] => {
  if (!Array.isArray(unicodes)) return [];

  const seen = new Set<string>();

  return unicodes.flatMap(unicode => {
    const emoji = findEmojiByUnicode(unicode);
    if (!emoji || seen.has(emoji.unicode)) return [];

    seen.add(emoji.unicode);
    return [emoji];
  });
};

export const applyEmojiSkinTone = (
  emoji: EmojiDisplayItem,
  skinTone: EmojiSkinTone = EMOJI_SKIN_TONE.Neutral
): EmojiDisplayItem => {
  const baseEmoji = findEmojiByUnicode(emoji.unicode) ?? emoji;
  const neutralEmoji = neutralEmojiByVariantCache?.get(baseEmoji.unicode) ?? baseEmoji;
  const modifier = EMOJI_SKIN_TONE_MODIFIERS[skinTone];

  if (!modifier || neutralEmoji.variants.length === 0) return neutralEmoji;

  const variant = neutralEmoji.variants.find(unicode => unicode.split("-").includes(modifier));
  return variant ? (findEmojiByUnicode(variant) ?? neutralEmoji) : neutralEmoji;
};

export const filterEmojis = ({
  category,
  recentEmojis = [],
  search = "",
  skinTone = EMOJI_SKIN_TONE.Neutral,
}: FilterEmojisOptions): EmojiDisplayItem[] => {
  const normalizedSearch = search.trim().toLowerCase();
  const allEmojis = getAllEmojis();
  let emojis: EmojiDisplayItem[];

  if (normalizedSearch) {
    emojis = allEmojis.filter(emoji => emoji.aliases.some(alias => alias.toLowerCase().includes(normalizedSearch)));
  } else if (category === EMOJI_CATEGORY.Recent) {
    emojis = resolveEmojiUnicodes(recentEmojis);
  } else if (category === EMOJI_CATEGORY.All) {
    emojis = allEmojis;
  } else if (isDataCategory(category)) {
    emojis = allEmojis.filter(emoji => emoji.category === category);
  } else {
    emojis = [];
  }

  const seen = new Set<string>();

  return emojis.flatMap(emoji => {
    const tonedEmoji = applyEmojiSkinTone(emoji, skinTone);
    if (seen.has(tonedEmoji.unicode)) return [];

    seen.add(tonedEmoji.unicode);
    return [tonedEmoji];
  });
};
